import { useEffect } from 'react'

import { useRecordHomepageVisit } from 'services/homepage'
import { useFeatureFlagService } from 'utils/useFeatureFlagService'

import type { PinTarget } from './usePinTarget'

/**
 * Fire-and-forget visit recording for the homepage Recents card.
 *
 * Records a visit each time the resolved target key changes, not only on
 * mount: detail routes (e.g. `/deals/:id`) keep the same component mounted
 * across param changes, so a mount-only effect would miss subsequent visits.
 * This is the documented exception to the repo's `useMountEffect`-only rule
 * (client/CLAUDE.md "No useEffect") — `useMountEffect` cannot express the
 * key-change re-fire, so a dependency-keyed `useEffect` is used deliberately.
 *
 * @param target - The page/record being visited, or null when not resolvable
 *   yet (e.g. an id still loading). Null targets record nothing.
 */
export function useRecordVisit(target: PinTarget | null): void {
  const { enableQuickAccess } = useFeatureFlagService()
  const recordVisit = useRecordHomepageVisit()
  const key =
    target === null
      ? null
      : target.targetType === 'page'
        ? `page:${target.pageKey}`
        : `${target.targetType}:${target.targetId}`

  useEffect(() => {
    if (!key || !enableQuickAccess || !target) return
    recordVisit.mutate(
      {
        data:
          target.targetType === 'page'
            ? { target_type: 'page', page_key: target.pageKey }
            : { target_type: target.targetType, target_id: target.targetId },
      },
      { onError: () => undefined }
    )
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [key, enableQuickAccess])
}
